import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Info, RotateCcw } from 'lucide-react'
import Header from '../components/Header'
import { useStore } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { showToast } from '../components/Toast'
import { isYokosoPointsEnabled } from '../lib/features'
import { isWeb3Mode } from '../lib/authMode'

const PRESET_AMOUNTS = [5000, 10000, 30000, 50000, 100000]

export default function TravelBudget() {
  const navigate = useNavigate()
  const { budgetEnabled, budgetAmount, getBudgetSpending, setBudget, resetBudget } = useStore()
  const { t } = useI18n()

  const [enabled, setEnabled] = useState(budgetEnabled)
  const [amount, setAmount] = useState(budgetAmount)
  const [customInput, setCustomInput] = useState('')
  const [showCustom, setShowCustom] = useState(!PRESET_AMOUNTS.includes(budgetAmount))

  const spending = getBudgetSpending()
  const percent = amount > 0 ? Math.min((spending / amount) * 100, 100) : 0
  const remaining = Math.max(amount - spending, 0)

  const barColor = percent >= 100 ? '#E63946' : percent >= 80 ? '#C9A84C' : '#4CAF50'

  const handleToggle = (newEnabled: boolean) => {
    setEnabled(newEnabled)
    setBudget(newEnabled, amount)
  }

  const handleSelectAmount = (value: number) => {
    setAmount(value)
    setShowCustom(false)
    setBudget(enabled, value)
  }

  const handleCustomConfirm = () => {
    const value = parseInt(customInput.replace(/\D/g, ''), 10)
    if (!isNaN(value) && value > 0) {
      setAmount(value)
      setBudget(enabled, value)
    }
  }

  const handleReset = () => {
    resetBudget()
    showToast({ type: 'success', message: t('budget.resetDone') })
  }

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header title={t('budget.title')} onBack={() => navigate(-1)} />

      <div className="flex-1 px-4 pb-8">
        {/* Spending Summary Card */}
        <div className="mt-4 rounded-2xl bg-gradient-to-r from-navy to-navy-light p-5 shadow-card">
          <p className="font-body text-xs text-white/70">{t('budget.currentSpending')}</p>
          <div className="mt-2 flex items-end gap-1">
            <span className="font-display text-3xl font-bold text-gold">
              &yen;{spending.toLocaleString()}
            </span>
            <span className="mb-1 font-body text-sm text-white/50">
              / &yen;{amount.toLocaleString()}
            </span>
          </div>

          {/* Progress bar */}
          <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${percent}%`, backgroundColor: barColor }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="font-body text-xs text-white/50">
              {t('budget.remaining')}: &yen;{remaining.toLocaleString()}
            </span>
            <span className="font-mono text-xs font-bold text-white/70">
              {Math.round(percent)}%
            </span>
          </div>

          {/* Disclaimer */}
          <p className="mt-3 border-t border-white/10 pt-2.5 font-body text-[10px] text-white/40">
            {t('budget.disclaimer')}
          </p>
        </div>

        {/* Enable Toggle */}
        <div className="mt-6 flex items-center justify-between rounded-xl bg-white px-4 py-4 shadow-card">
          <span className="font-body text-sm text-text-primary">
            {t('budget.enable')}
          </span>
          <button
            onClick={() => handleToggle(!enabled)}
            className={`relative h-7 w-12 rounded-full transition-colors duration-200 ${
              enabled ? 'bg-matcha' : 'bg-gray-300'
            }`}
          >
            <div
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-transform duration-200 ${
                enabled ? 'translate-x-5.5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Budget Amount Selection */}
        {enabled && (
          <div className="animate-fade-in mt-4">
            <p className="mb-3 font-display text-sm font-semibold text-text-primary">
              {t('budget.amount')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {PRESET_AMOUNTS.map((value) => (
                <button
                  key={value}
                  onClick={() => handleSelectAmount(value)}
                  className={`tap-effect rounded-xl border-2 py-3 font-display text-sm font-semibold transition-colors ${
                    amount === value && !showCustom
                      ? 'border-navy bg-navy text-white'
                      : 'border-gray-200 bg-white text-text-primary hover:border-navy/40'
                  }`}
                >
                  &yen;{value.toLocaleString()}
                </button>
              ))}
              <button
                onClick={() => setShowCustom(true)}
                className={`tap-effect rounded-xl border-2 py-3 font-display text-sm font-semibold transition-colors ${
                  showCustom
                    ? 'border-navy bg-navy text-white'
                    : 'border-gray-200 bg-white text-text-primary hover:border-navy/40'
                }`}
              >
                {t('budget.custom')}
              </button>
            </div>

            {/* Custom Amount Input */}
            {showCustom && (
              <div className="mt-3 flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-tertiary">
                    &yen;
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="50000"
                    className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-7 pr-3 font-mono text-sm text-text-primary focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                  />
                </div>
                <button
                  onClick={handleCustomConfirm}
                  disabled={!customInput || parseInt(customInput, 10) <= 0}
                  className="tap-effect rounded-lg bg-navy px-5 py-2.5 font-body text-sm font-medium text-white transition-colors hover:bg-navy-light disabled:opacity-40"
                >
                  OK
                </button>
              </div>
            )}

            {/* Reset Button */}
            <button
              onClick={handleReset}
              className="tap-effect mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3 font-body text-sm font-medium text-text-secondary shadow-card transition-colors hover:bg-gray-50"
            >
              <RotateCcw size={16} />
              {t('budget.reset')}
            </button>
          </div>
        )}

        {/* ポイント (YOKOSO 通貨) で払うときの予算の目安。ポイント払いが使えない環境 (本番) と、残高が本物の JPYC の
            Web3 のモードでは出さない (#2571) */}
        {isYokosoPointsEnabled() && !isWeb3Mode() && (
          <div className="mt-6 flex gap-3 rounded-xl bg-navy/5 p-4">
            <Info size={18} className="mt-0.5 shrink-0 text-navy/50" />
            <p className="font-body text-xs leading-relaxed text-text-secondary">
              {t('budget.pointsTip')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
